// Report Templates Service
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';
// Translation function will be passed as parameter when needed

export const reportTemplatesService = {
  /**
   * Get all report templates for a company
   */
  async getTemplates(companyId) {
    try {
      const templatesRef = collection(db, 'reportTemplates');

      // Try to get templates with ordering first
      let snapshot;
      try {
        const q = query(
          templatesRef,
          where('companyId', '==', companyId),
          orderBy('createdAt', 'desc')
        );
        snapshot = await getDocs(q);
      } catch (indexError) {
        console.warn('Index not available, querying without orderBy:', indexError);
        // Fallback to query without orderBy if index doesn't exist
        const q = query(
          templatesRef,
          where('companyId', '==', companyId)
        );
        snapshot = await getDocs(q);
      }

      const templates = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // If no templates exist, create a default one
      if (templates.length === 0) {
        console.log('No templates found, creating default template...');
        const defaultTemplateId = await this.createDefaultTemplate(companyId, 'system');
        const defaultTemplate = await this.getTemplate(defaultTemplateId);
        return defaultTemplate ? [defaultTemplate] : [];
      }

      return templates;
    } catch (error) {
      console.error('Error getting report templates:', error);
      // Return empty array instead of throwing to prevent UI crash
      return [];
    }
  },

  /**
   * Get a specific template
   */
  async getTemplate(templateId) {
    try {
      const templateRef = doc(db, 'reportTemplates', templateId);
      const snapshot = await getDoc(templateRef);
      if (snapshot.exists()) {
        return { id: snapshot.id, ...snapshot.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting report template:', error);
      throw error;
    }
  },

  /**
   * Create a new report template
   */
  async createTemplate(templateData) {
    try {
      const templatesRef = collection(db, 'reportTemplates');
      const docRef = await addDoc(templatesRef, {
        ...templateData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating report template:', error);
      throw error;
    }
  },

  /**
   * Update a report template
   */
  async updateTemplate(templateId, templateData) {
    try {
      const templateRef = doc(db, 'reportTemplates', templateId);
      await updateDoc(templateRef, {
        ...templateData,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error updating report template:', error);
      throw error;
    }
  },

  /**
   * Delete a report template
   */
  async deleteTemplate(templateId) {
    try {
      const templateRef = doc(db, 'reportTemplates', templateId);
      await deleteDoc(templateRef);
      return true;
    } catch (error) {
      console.error('Error deleting report template:', error);
      throw error;
    }
  },

  /**
   * Get default template structure
   */
  getDefaultTemplate(t) {
    // Optional translator `t` allows UI to show localized default labels
    const L = (key, fallback) => (typeof t === 'function' ? t(key, fallback) : fallback);
    return {
      name: 'Default Shipments Report',
      description: 'Standard shipment report with basic fields',
      type: 'shipments',
      fields: {
        no: { enabled: true, label: L('reports.field_labels.no', 'No.'), order: 1 },
        shipmentNumber: { enabled: true, label: L('reports.field_labels.shipmentNumber', 'Shipment #'), order: 2 },
        date: { enabled: true, label: L('reports.field_labels.date', 'Date'), order: 3 },
        truckPlate: { enabled: true, label: L('reports.field_labels.truckPlate', 'Truck Plate'), order: 4 },
        truckKind: { enabled: true, label: L('reports.field_labels.truckKind', 'Truck Kind'), order: 5 },
        pickupSite: { enabled: true, label: L('reports.field_labels.pickupSite', 'Pickup Site'), order: 6 },
        dropoffSite: { enabled: true, label: L('reports.field_labels.dropoffSite', 'Dropoff Site'), order: 7 },
        stopsCount: { enabled: true, label: L('reports.field_labels.stopsCount', 'Number of Stops'), order: 8 },
        stopsList: { enabled: true, label: L('reports.field_labels.stopsList', 'Stops (raw)'), order: 8.2 },
        stopsByCity: { enabled: true, label: L('reports.field_labels.stopsByCity', 'Stops by City'), order: 8.3 },
        originCity: { enabled: true, label: L('reports.field_labels.originCity', 'Origin City'), order: 9 },
        destinationCities: { enabled: true, label: L('reports.field_labels.destinationCities', 'Destination Cities'), order: 10 },
        backhaulPrice: { enabled: true, label: L('reports.field_labels.backhaulPrice', 'Backhaul Price'), order: 11 },
        itemizedCosts: { enabled: true, label: L('reports.field_labels.itemizedCosts', 'Itemized Costs'), order: 12 },
        basePrice: { enabled: true, label: L('reports.field_labels.basePrice', 'Base Price'), order: 13 },
        totalPrice: { enabled: true, label: L('reports.field_labels.totalPrice', 'Total Price'), order: 14 },
        clientName: { enabled: true, label: L('reports.field_labels.clientName', 'Client Name'), order: 15 },
        requesterName: { enabled: false, label: L('reports.field_labels.requesterName', 'Service Requester'), order: 15.5 },
        driverName: { enabled: true, label: L('reports.field_labels.driverName', 'Driver Name'), order: 16 },
        status: { enabled: true, label: L('reports.field_labels.status', 'Status'), order: 17 },
        paymentStatus: { enabled: true, label: L('reports.field_labels.paymentStatus', 'Payment Status'), order: 18 },
        notes: { enabled: true, label: L('reports.field_labels.notes', 'Notes'), order: 19 },
        // Optional fields (disabled by default)
        isInternal: { enabled: false, label: L('reports.field_labels.isInternal', 'Internal/External'), order: 20 },
        totalCost: { enabled: false, label: L('reports.field_labels.totalCost', 'Total Cost'), order: 21 },
        fuelCost: { enabled: false, label: L('reports.field_labels.fuelCost', 'Fuel Cost'), order: 22 },
        commission: { enabled: false, label: L('reports.field_labels.commission', 'Commission'), order: 23 },
        maintenanceCost: { enabled: false, label: L('reports.field_labels.maintenanceCost', 'Maintenance Cost'), order: 24 },
        vehicleInfo: { enabled: false, label: L('reports.field_labels.vehicleInfo', 'Vehicle Info'), order: 25 },
        deliveryDate: { enabled: false, label: L('reports.field_labels.deliveryDate', 'Delivery Date'), order: 26 },
        distance: { enabled: false, label: L('reports.field_labels.distance', 'Distance'), order: 27 }
      },
      clientSpecific: false,
      clientId: null,
      isDefault: true
    };
  },

  /**
   * Create default template for a company
   */
  async createDefaultTemplate(companyId, createdBy) {
    const defaultTemplate = this.getDefaultTemplate();
    return await this.createTemplate({
      ...defaultTemplate,
      companyId,
      createdBy
    });
  },

  /**
   * Get templates for a specific client
   */
  async getClientTemplates(companyId, clientId) {
    try {
      const templatesRef = collection(db, 'reportTemplates');
      const q = query(
        templatesRef,
        where('companyId', '==', companyId),
        where('clientSpecific', '==', true),
        where('clientId', '==', clientId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting client templates:', error);
      throw error;
    }
  },

  /**
   * Duplicate a template
   */
  async duplicateTemplate(templateId, newName, companyId, createdBy) {
    try {
      const originalTemplate = await this.getTemplate(templateId);
      if (!originalTemplate) {
        throw new Error('Template not found');
      }

      const duplicatedTemplate = {
        ...originalTemplate,
        name: newName,
        companyId,
        createdBy,
        isDefault: false
      };

      // Remove the id from the duplicated template
      delete duplicatedTemplate.id;
      delete duplicatedTemplate.createdAt;
      delete duplicatedTemplate.updatedAt;

      return await this.createTemplate(duplicatedTemplate);
    } catch (error) {
      console.error('Error duplicating template:', error);
      throw error;
    }
  },

  /**
   * Merge a stored template with current-language defaults so the modal shows
   * localized labels and any newly-added fields, while keeping the user's
   * enabled/order choices and any custom labels they explicitly renamed.
   */
  applyLocalizedDefaults(template, t) {
    const defaults = this.getDefaultTemplate(t).fields;
    const stored = template?.fields || {};
    const merged = {};
    Object.entries(defaults).forEach(([key, def]) => {
      const s = stored[key];
      if (!s) {
        merged[key] = def;
        return;
      }
      // Heuristic: if the stored label matches any other locale's default for
      // this key, treat it as "not customized" and overwrite with the current
      // language's default. Otherwise keep the user's custom label.
      const customized = s.label && s.label !== def.label;
      merged[key] = {
        enabled: s.enabled !== undefined ? s.enabled : def.enabled,
        label: customized ? s.label : def.label,
        order: s.order !== undefined ? s.order : def.order,
      };
    });
    return { ...template, fields: merged };
  },

  /**
   * Get available field options for templates
   */
  getAvailableFields(t) {
    const L = (key, fallback) => (typeof t === 'function' ? t(key, fallback) : fallback);
    return {
      basic: {
        no: { label: L('reports.field_labels.no', 'No.'), description: L('reports.field_descriptions.no', 'Sequential number') },
        shipmentNumber: { label: L('reports.field_labels.shipmentNumber', 'Shipment #'), description: L('reports.field_descriptions.shipmentNumber', 'Shipment tracking number') },
        date: { label: L('reports.field_labels.date', 'Date'), description: L('reports.field_descriptions.date', 'Pickup/creation date') },
        truckPlate: { label: L('reports.field_labels.truckPlate', 'Truck Plate'), description: L('reports.field_descriptions.truckPlate', 'Vehicle license plate') },
        truckKind: { label: L('reports.field_labels.truckKind', 'Truck Kind'), description: L('reports.field_descriptions.truckKind', 'Type of truck (e.g., Dina Refrigerator, Bus)') },
        pickupSite: { label: L('reports.field_labels.pickupSite', 'Pickup Site'), description: L('reports.field_descriptions.pickupSite', 'Pickup location/site') },
        dropoffSite: { label: L('reports.field_labels.dropoffSite', 'Dropoff Site'), description: L('reports.field_descriptions.dropoffSite', 'Dropoff location/site') },
        stopsCount: { label: L('reports.field_labels.stopsCount', 'Number of Stops'), description: L('reports.field_descriptions.stopsCount', 'Stop count formatted as "N stops"') },
        stopsList: { label: L('reports.field_labels.stopsList', 'Stops (raw)'), description: L('reports.field_descriptions.stopsList', 'All stop fields across cities, joined by " - "') },
        stopsByCity: { label: L('reports.field_labels.stopsByCity', 'Stops by City'), description: L('reports.field_descriptions.stopsByCity', 'Stops grouped per city, e.g. "Dammam: 5544 - 0099, Riyadh: 9988"') },
        originCity: { label: L('reports.field_labels.originCity', 'Origin City'), description: L('reports.field_descriptions.originCity', 'Starting city') },
        destinationCities: { label: L('reports.field_labels.destinationCities', 'Destination Cities'), description: L('reports.field_descriptions.destinationCities', 'Destination cities (comma-separated if multiple)') },
        backhaulPrice: { label: L('reports.field_labels.backhaulPrice', 'Backhaul Price'), description: L('reports.field_descriptions.backhaulPrice', 'Return trip pricing') },
        itemizedCosts: { label: L('reports.field_labels.itemizedCosts', 'Itemized Costs'), description: L('reports.field_descriptions.itemizedCosts', 'Sum of all itemized costs') },
        basePrice: { label: L('reports.field_labels.basePrice', 'Base Price'), description: L('reports.field_descriptions.basePrice', 'Base shipment cost') },
        totalPrice: { label: L('reports.field_labels.totalPrice', 'Total Price'), description: L('reports.field_descriptions.totalPrice', 'Total price including VAT') },
        clientName: { label: L('reports.field_labels.clientName', 'Client Name'), description: L('reports.field_descriptions.clientName', 'Client company name') },
        requesterName: { label: L('reports.field_labels.requesterName', 'Service Requester'), description: L('reports.field_descriptions.requesterName', 'Person who requested the service') },
        driverName: { label: L('reports.field_labels.driverName', 'Driver Name'), description: L('reports.field_descriptions.driverName', 'Driver name') },
        status: { label: L('reports.field_labels.status', 'Status'), description: L('reports.field_descriptions.status', 'Shipment status') },
        paymentStatus: { label: L('reports.field_labels.paymentStatus', 'Payment Status'), description: L('reports.field_descriptions.paymentStatus', 'Payment status') },
        notes: { label: L('reports.field_labels.notes', 'Notes'), description: L('reports.field_descriptions.notes', 'Shipment notes') }
      },
      optional: {
        requesterName: { label: L('reports.field_labels.requesterName', 'Service Requester'), description: L('reports.field_descriptions.requesterName', 'Person who requested the service') },
        isInternal: { label: L('reports.field_labels.isInternal', 'Internal/External'), description: L('reports.field_descriptions.isInternal', 'Driver type indicator') },
        totalCost: { label: L('reports.field_labels.totalCost', 'Total Cost'), description: L('reports.field_descriptions.totalCost', 'Sum of all costs') },
        fuelCost: { label: L('reports.field_labels.fuelCost', 'Fuel Cost'), description: L('reports.field_descriptions.fuelCost', 'Fuel expenses from shipment costing') },
        commission: { label: L('reports.field_labels.commission', 'Commission'), description: L('reports.field_descriptions.commission', 'Driver commission from shipment costing') },
        maintenanceCost: { label: L('reports.field_labels.maintenanceCost', 'Maintenance Cost'), description: L('reports.field_descriptions.maintenanceCost', 'Maintenance expenses from shipment costing') },
        vehicleInfo: { label: L('reports.field_labels.vehicleInfo', 'Vehicle Info'), description: L('reports.field_descriptions.vehicleInfo', 'Vehicle details') },
        deliveryDate: { label: L('reports.field_labels.deliveryDate', 'Delivery Date'), description: L('reports.field_descriptions.deliveryDate', 'Expected/actual delivery date') },
        distance: { label: L('reports.field_labels.distance', 'Distance'), description: L('reports.field_descriptions.distance', 'Total distance') }
      }
    };
  }
};